import { useState } from "react";

import {
  IHospital,
  ISyncTenantIdsResult,
} from "../../../@types/admin/AdminHospital";
import { useSyncTenantIds } from "../../../hooks/mutations/admin/hospitals";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  FieldHint,
  FilterButton,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../shared";
import { ModalBodyText, SyncResultList } from "./AdminHospitals.styles";

interface ISyncTenantIdsModalProps {
  hospital: IHospital;
  onClose: () => void;
}

/**
 * Confirmation for the Sync Tenant IDs maintenance action (EN-5152).
 *
 * Retool ran this behind a one-line "Are you sure?" and reported only
 * "Successfully sync all tenant IDs". It is a bulk write over live Cognito
 * accounts, so the prompt names the hospital and the result reports what
 * actually happened — the endpoint returns per-run counts, including
 * failures, which the Retool toast threw away.
 *
 * Despite the endpoint's name the write is scoped to this one hospital group.
 */
export function SyncTenantIdsModal({
  hospital,
  onClose,
}: ISyncTenantIdsModalProps) {
  const { showToast } = useToast();
  const { syncTenantIds, isSyncingTenantIds } = useSyncTenantIds();
  const [result, setResult] = useState<ISyncTenantIdsResult | null>(null);

  const handleSync = async () => {
    try {
      const syncResult = await syncTenantIds(Number(hospital.id));
      setResult(syncResult);

      if (syncResult.cognitoUpdatesFailed > 0) {
        showToast(
          `Tenant IDs synced with ${syncResult.cognitoUpdatesFailed} failure(s).`,
          "warning"
        );
      } else {
        showToast("Tenant IDs synced successfully.", "success");
      }
    } catch (error) {
      showToast(
        errorToastMessage(error, "There was an error syncing the tenant IDs."),
        "warning"
      );
    }
  };

  if (result) {
    return (
      <ModalContainer>
        <ModalTitle>Sync tenant IDs</ModalTitle>
        <ModalForm>
          <ModalBodyText>
            Synced <strong>{hospital.name}</strong>.
          </ModalBodyText>
          <SyncResultList>
            <li>
              {result.cognitoUpdatesSuccessful} of{" "}
              {result.cognitoUpdatesAttempted} Cognito accounts updated
            </li>
            <li>
              {result.uniqueEmailsFound} unique accounts ({result.patientsFound}{" "}
              patients, {result.staffFound} staff)
            </li>
            {result.cognitoUpdatesFailed > 0 && (
              <li>{result.cognitoUpdatesFailed} failed</li>
            )}
          </SyncResultList>
          {result.errors.length > 0 && (
            <FieldHint>{result.errors.slice(0, 3).join(" · ")}</FieldHint>
          )}
          <ModalFormActions>
            <FilterButton variant="primary" onClick={onClose}>
              Done
            </FilterButton>
          </ModalFormActions>
        </ModalForm>
      </ModalContainer>
    );
  }

  return (
    <ModalContainer>
      <ModalTitle>Sync tenant IDs</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Push <strong>{hospital.name}</strong>&apos;s tenant ID onto every
          Cognito account under it — patients and staff.
        </ModalBodyText>
        <WarningNotice>
          This rewrites live accounts and can&apos;t be undone from here.
        </WarningNotice>
        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSyncingTenantIds}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isSyncingTenantIds}
            onClick={handleSync}
          >
            {isSyncingTenantIds ? "Syncing..." : "Sync tenant IDs"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
